import React, { useEffect, useState, useContext } from "react";
import { Button, Table, Modal, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { AuthContext } from "../../hooks/auth/AuthContext";
import Head from "../../components/layout/head/Head";
import { toast } from "react-toastify";
import api from "../../services/api";

const PuntosExpedicionPage = () => {
    const authContext = useContext(AuthContext);
    const { user } = authContext;

    const [puntos, setPuntos] = useState([]);
    const [establecimientos, setEstablecimientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [incluirInactivos, setIncluirInactivos] = useState(false);
    const [filtroEstablecimiento, setFiltroEstablecimiento] = useState("");

    // Form state
    const [formData, setFormData] = useState({
        id: "",
        dPunExp: "",
        descripcion: "",
        establecimientoId: "",
        activo: true,
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [incluirInactivos, filtroEstablecimiento]);

    const loadData = async () => {
        try {
            setLoading(true);
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` }
            };

            // Cargar establecimientos
            const establResponse = await api.get("/api/establecimientos", {
                ...config,
                params: { soloActivos: true }
            });
            setEstablecimientos(establResponse.data);

            // Cargar puntos de expedición
            const puntosParams = { soloActivos: !incluirInactivos };
            if (filtroEstablecimiento) {
                puntosParams.establecimientoId = filtroEstablecimiento;
            }

            const puntosResponse = await api.get("/api/puntos-expedicion", {
                ...config,
                params: puntosParams
            });
            setPuntos(puntosResponse.data);
        } catch (error) {
            console.error("Error al cargar datos:", error);
            toast.error("Error al cargar los puntos de expedición");
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = async (item = null) => {
        if (item) {
            setEditMode(true);
            setCurrentItem(item);
            setFormData({
                id: item.id,
                dPunExp: item.dPunExp || "",
                descripcion: item.descripcion || "",
                establecimientoId: item.establecimientoId || "",
                activo: item.activo !== undefined ? item.activo : true,
            });
        } else {
            setEditMode(false);
            setCurrentItem(null);
            setFormData({
                id: "",
                dPunExp: "",
                descripcion: "",
                establecimientoId: "",
                activo: true,
            });
        }
        setFormErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.dPunExp || formData.dPunExp.trim() === "") {
            errors.dPunExp = "El código de punto de expedición es obligatorio";
        } else if (formData.dPunExp.length !== 3) {
            errors.dPunExp = "El código debe tener exactamente 3 dígitos";
        }

        if (!formData.descripcion || formData.descripcion.trim() === "") {
            errors.descripcion = "La descripción es obligatoria";
        }

        if (!formData.establecimientoId) {
            errors.establecimientoId = "Debe seleccionar un establecimiento";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` }
            };

            const dataToSend = {
                DPunExp: formData.dPunExp,
                Descripcion: formData.descripcion,
                EstablecimientoId: formData.establecimientoId,
                Activo: formData.activo,
            };

            if (editMode) {
                dataToSend.Id = formData.id;
                await api.put(`/api/puntos-expedicion/${formData.id}`, dataToSend, config);
                toast.success("Punto de expedición actualizado correctamente");
            } else {
                await api.post("/api/puntos-expedicion", dataToSend, config);
                toast.success("Punto de expedición creado correctamente");
            }

            handleCloseModal();
            loadData();
        } catch (error) {
            console.error("Error al guardar punto de expedición:", error);
            if (error.response?.data) {
                toast.error(typeof error.response.data === 'string' ? error.response.data : "Error al guardar el punto de expedición");
            } else {
                toast.error("Error al guardar el punto de expedición");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const getEstablecimientoNombre = (establecimientoId) => {
        const est = establecimientos.find(e => e.id === establecimientoId);
        return est ? `${est.dEst} - ${est.descripcion}` : "N/A";
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            <Head title="Configuración - Puntos de Expedición" />
            <div className="container-fluid mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2>Gestión de Puntos de Expedición</h2>
                    <div className="d-flex gap-3 align-items-center">
                        <Form.Select
                            value={filtroEstablecimiento}
                            onChange={(e) => setFiltroEstablecimiento(e.target.value)}
                            style={{ width: "250px" }}
                        >
                            <option value="">Todos los establecimientos</option>
                            {establecimientos.map(est => (
                                <option key={est.id} value={est.id}>
                                    {est.dEst} - {est.descripcion}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Check
                            type="checkbox"
                            label="Incluir inactivos"
                            checked={incluirInactivos}
                            onChange={(e) => setIncluirInactivos(e.target.checked)}
                        />
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            Nuevo Punto de Expedición
                        </Button>
                    </div>
                </div>

                {puntos.length === 0 ? (
                    <Alert variant="info">No hay puntos de expedición registrados.</Alert>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Descripción</th>
                                    <th>Establecimiento</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {puntos.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.dPunExp}</td>
                                        <td>{item.descripcion}</td>
                                        <td>{getEstablecimientoNombre(item.establecimientoId)}</td>
                                        <td>
                                            <Badge bg={item.activo ? "success" : "secondary"}>
                                                {item.activo ? "Activo" : "Inactivo"}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleShowModal(item)}
                                            >
                                                Editar
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Modal para Crear/Editar */}
                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editMode ? "Editar Punto de Expedición" : "Nuevo Punto de Expedición"}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Código Punto de Expedición (3 dígitos) *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dPunExp"
                                    value={formData.dPunExp}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.dPunExp}
                                    maxLength={3}
                                    placeholder="Ej: 001"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.dPunExp}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Descripción *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.descripcion}
                                    placeholder="Ej: Caja 1"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.descripcion}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Establecimiento *</Form.Label>
                                <Form.Select
                                    name="establecimientoId"
                                    value={formData.establecimientoId}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.establecimientoId}
                                >
                                    <option value="">Seleccione un establecimiento</option>
                                    {establecimientos.map(est => (
                                        <option key={est.id} value={est.id}>
                                            {est.dEst} - {est.descripcion}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.establecimientoId}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {editMode && (
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="activo"
                                        label="Activo"
                                        checked={formData.activo}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Guardando...
                                    </>
                                ) : editMode ? (
                                    "Actualizar"
                                ) : (
                                    "Crear"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default PuntosExpedicionPage;
